import React from 'react';
import { useUserStatus } from '../../contexts/UserStatusContext';

const RemainingLinksCard = ({ className = '', style = {}, onUpgradeClick = null }) => {
  const { 
    userStatus, 
    remainingLinks,
    isLoadingGenerations
  } = useUserStatus();

  // Add CSS animation for spinner
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  return (
    <div
      className={className}
      style={{
        backgroundColor: '#ffffff',
        padding: '24px',
        borderRadius: '12px',
        border: '1px solid #f1f3f4',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        ...style
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
          <svg 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            style={{ marginRight: '8px', color: userStatus === 'pro' ? '#10b981' : '#f59e0b' }}
          >
            <path 
              d="M13 2L3 14H12L11 22L21 10H12L13 2Z" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
          <h4 
            style={{ 
              margin: 0, 
              fontSize: '1.375rem', 
              fontWeight: '600', 
              color: '#1a1a1a',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
            }}
          >
            Link Generations
          </h4>
        </div>
        <p 
          style={{ 
            margin: 0, 
            fontSize: '14px', 
            color: '#6b7280',
            lineHeight: '1.5'
          }}
        >
          {userStatus === 'pro' 
            ? 'Unlimited link generations with your Pro plan'
            : 'Free link generations remaining this week'
          }
        </p>
      </div>

      {/* Count Display */}
      <div 
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '16px'
        }}
      >
        <div
          style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: isLoadingGenerations ? '#f9fafb' : 
                              userStatus === 'pro' ? '#ecfdf5' : '#fefce8',
            border: `2px solid ${isLoadingGenerations ? '#d1d5db' : 
                                 userStatus === 'pro' ? '#10b981' : '#f59e0b'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}
        >
          {isLoadingGenerations ? (
            <div
              style={{
                width: '24px',
                height: '24px',
                border: '2px solid #e5e7eb',
                borderTop: '2px solid #3b82f6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}
            />
          ) : userStatus === 'pro' ? (
            <div
              style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#10b981',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
              }}
            >
              ∞
            </div>
          ) : (
            <div
              style={{
                fontSize: '28px',
                fontWeight: '700',
                color: remainingLinks > 0 ? '#f59e0b' : '#ef4444',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
              }}
            >
              {remainingLinks}
            </div>
          )}
        </div>
      </div>

      {/* Status Information */}
      <div
        style={{
          padding: '12px',
          backgroundColor: isLoadingGenerations ? '#f9fafb' :
                           userStatus === 'pro' ? '#ecfdf5' : 
                           remainingLinks > 0 ? '#fefce8' : '#fef2f2',
          borderRadius: '8px',
          border: `1px solid ${isLoadingGenerations ? '#d1d5db' :
                                userStatus === 'pro' ? '#10b981' : 
                                remainingLinks > 0 ? '#f59e0b' : '#ef4444'}`,
          textAlign: 'center'
        }}
      >
        {isLoadingGenerations ? (
          <div>
            <div 
              style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                color: '#6b7280',
                marginBottom: '4px'
              }}
            >
              🔄 Loading...
            </div>
            <div 
              style={{ 
                fontSize: '12px', 
                color: '#374151' 
              }}
            >
              Fetching your remaining generations
            </div>
          </div>
        ) : userStatus === 'pro' ? (
          <div>
            <div 
              style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                color: '#10b981',
                marginBottom: '4px'
              }}
            >
              🚀 Pro Member
            </div>
            <div 
              style={{ 
                fontSize: '12px', 
                color: '#374151' 
              }}
            >
              Create unlimited trackable links
            </div>
          </div>
        ) : remainingLinks > 0 ? (
          <div>
            <div 
              style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                color: '#f59e0b',
                marginBottom: '4px'
              }}
            >
              ⚡ {remainingLinks} {remainingLinks === 1 ? 'Link' : 'Links'} Remaining
            </div>
            <div 
              style={{ 
                fontSize: '12px', 
                color: '#374151' 
              }}
            >
              Free generations reset weekly
            </div>
          </div>
        ) : (
          <div>
            <div 
              style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                color: '#ef4444',
                marginBottom: '4px'
              }}
            >
              🚫 Limit Reached
            </div>
            <div 
              style={{ 
                fontSize: '12px', 
                color: '#374151' 
              }}
            >
              Upgrade to Pro for unlimited links
            </div>
          </div>
        )}
      </div>

      {/* Upgrade CTA for Starter users with no links remaining */}
      {!isLoadingGenerations && userStatus === 'starter' && remainingLinks === 0 && (
        <div style={{ marginTop: '16px' }}>
          <button
            style={{
              width: '100%',
              padding: '12px 16px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
            onClick={() => {
              if (onUpgradeClick) {
                onUpgradeClick();
              } else {
                // TODO: Navigate to payment/upgrade page
                console.log('Navigate to upgrade page');
              }
            }}
          >
            Upgrade to Pro
          </button>
        </div>
      )}
    </div>
  );
};

export default RemainingLinksCard;